import styled from 'styled-components';

export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
`;

export const Header = styled.header`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 1rem 4rem;
  background-color: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  position: sticky;
  top: 0;
  z-index: 100;

  /* Between the desktop layout and the Nav-hide breakpoint below, nothing was
     shrinking (fixed padding + fixed gaps), so on common laptop widths
     (1280 / 1366 / 1440) the auth buttons overflowed past the viewport edge
     and got clipped. Trim padding/gaps here so the row keeps fitting. */
  @media (max-width: 1400px) {
    padding: 1rem 2rem;
  }

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

export const Logo = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
`;

export const Nav = styled.nav`
  display: flex;
  gap: 2.25rem;
  align-items: center;
  margin-left: auto;
  margin-right: 2.5rem;

  @media (max-width: 1400px) {
    gap: 1.25rem;
    margin-right: 1.5rem;
  }

  @media (max-width: 960px) {
    display: none;
  }
`;

export const NavLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.gray700};
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: color 0.15s;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const AuthButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-shrink: 0;
`;

/* =============================== 0. HERO ===================================
   Pixel-recreation of the design handoff: "Tvikla Hero" hub-spoke diagram.
   Desktop layout is exact (1fr / 2px / 1.08fr, 1440px target); below that it
   stacks to a single column and the diagram scales down as a unit. */

export const HeroGrid = styled.section`
  display: grid;
  grid-template-columns: 1fr 2px 1.08fr;
  /* 820px was taller than the copy column ever needed, forcing the whole
     hero (plus the header above it) past the available height on a real
     13" laptop window — actual browser chrome (tab/address/bookmarks bars)
     eats more vertical space than a bare viewport-height number accounts
     for, so "fits in a 956px-tall display" and "fits without scrolling in
     a real browser window at that display" are not the same target. 680px
     plus the trimmed copy spacing below (see HeroTitle/HeroSubtitle/
     HeroCtaRow/HeroMetaRow) keeps the whole hero, including the bottom
     meta row, comfortably above that real fold instead of just barely
     under a bare-viewport estimate. */
  min-height: 680px;
  background: #ffffff;
  font-family: 'Archivo', ${({ theme }) => theme.typography.fontFamily.primary};

  @media (max-width: 1439px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    min-height: auto;
  }
`;

export const HeroDivider = styled.div`
  background: #f2691f;

  @media (max-width: 1439px) {
    display: none;
  }
`;

// A fixed `height` at each tier below, sized to fully contain the diagram
// after HeroDiagramScaler's transform: scale() (640px * that tier's scale
// — the diagram's real 640px canvas). The previous 480px default cropped
// the top row against the nav bar and clipped the bottom row (Compliance/
// Customs) almost entirely — 480 was never big enough for the unscaled
// diagram, at any viewport. transform shrinks the diagram visually but
// never affects layout sizing, so the unscaled 640px box was still forcing
// this column's intrinsic height at every tier. min-height alone can't cap
// that (it only raises a floor), and per the CSS spec min-height also
// always wins over a smaller height — so min-height must shrink alongside
// height at each tier, or a taller floor silently cancels the override.
export const HeroDiagramColumn = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  /* Deliberately only a floor here, no explicit height/align-self: at the
     two-column desktop width this and HeroCopyColumn are siblings in the
     same grid row, and HeroGrid sets no align-items, so its stretch default
     applies. Both columns end up exactly the row's real height (driven by
     HeroCopyColumn's own content, floored at HeroGrid's min-height: 820px)
     and each centers its own content within that shared height — which is
     what actually keeps them level, instead of guessing a fixed pixel
     height for this column and hoping it happens to land on the copy
     column's real height. A previous attempt gave this column its own
     explicit height + align-self: center, which produced whitespace above
     the diagram and let it overhang past HeroCopyColumn's buttons below —
     an explicit height here can only ever coincidentally match the
     sibling's real height, never guarantee it. min-height only needs to
     clear 640px so the diagram itself is never cropped. */
  min-height: 640px;
  background: radial-gradient(120% 100% at 50% 45%, #fff4ec 0%, #fffbf8 45%, #ffffff 100%);

  @media (max-width: 1439px) {
    height: 525px;
    min-height: 525px;
  }
  @media (max-width: 1024px) {
    height: 397px;
    min-height: 397px;
  }
  @media (max-width: 768px) {
    height: 307px;
    min-height: 307px;
  }
  @media (max-width: 480px) {
    height: 230px;
    min-height: 230px;
  }
`;

export const HeroDiagramScaler = styled.div`
  transform: scale(1);

  @media (max-width: 1439px) {
    transform: scale(0.82);
  }
  @media (max-width: 1024px) {
    transform: scale(0.62);
  }
  @media (max-width: 768px) {
    transform: scale(0.48);
  }
  @media (max-width: 480px) {
    transform: scale(0.36);
  }
`;

export const HeroCopyColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px 88px;
  position: relative;
  overflow: hidden;

  @media (max-width: 1100px) {
    padding: 64px 48px;
  }
  @media (max-width: 768px) {
    padding: 48px 24px;
  }
`;

export const HeroEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  align-self: flex-start;
  padding: 11px 20px;
  border-radius: 999px;
  background: #fef0e7;
  color: #c94e0c;
  font-size: 16px;
  font-weight: 600;
`;

export const HeroTitle = styled.h1`
  margin: 20px 0 0;
  font-size: 68px;
  line-height: 1.06;
  letter-spacing: -0.025em;
  font-weight: 800;
  color: #0f1b2d;

  span {
    color: #f2691f;
  }

  @media (max-width: 1100px) {
    font-size: 46px;
  }
  @media (max-width: 600px) {
    font-size: 36px;
  }
`;

export const HeroSubtitle = styled.p`
  margin: 18px 0 0;
  max-width: 640px;
  font-size: 19px;
  line-height: 1.7;
  color: #55637a;
`;

export const HeroCtaRow = styled.div`
  display: flex;
  gap: 18px;
  margin-top: 28px;
  flex-wrap: wrap;
`;

export const HeroPrimaryButton = styled.button`
  border: 0;
  border-radius: 0;
  padding: 20px 34px;
  background: #f2691f;
  color: #ffffff;
  font-family: inherit;
  font-size: 19px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 12px 28px rgba(242, 105, 31, 0.28);
  transition: background 0.15s;

  &:hover {
    background: #dc4c06;
  }
  &:focus-visible {
    outline: 2px solid #f2691f;
    outline-offset: 2px;
  }
`;

export const HeroSecondaryButton = styled.button`
  border-radius: 0;
  padding: 20px 34px;
  background: #ffffff;
  border: 1.5px solid #dde3ec;
  color: #0f1b2d;
  font-family: inherit;
  font-size: 19px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: #f2691f;
    color: #f2691f;
  }
  &:focus-visible {
    outline: 2px solid #f2691f;
    outline-offset: 2px;
  }
`;

export const HeroMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px 32px;
  margin-top: 32px;
  padding-top: 18px;
  border-top: 2px solid #f0f2f5;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #8e99ab;
  text-transform: uppercase;
`;

/* =============================== STATEMENT =================================
   One bold assertion, right after the hero: you don't need a dozen tools. */

export const StatementSection = styled.section`
  padding: 3.5rem 4rem;
  background: ${({ theme }) => theme.colors.primary50};
  border-top: 1px solid ${({ theme }) => theme.colors.primary100};
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary100};
  text-align: center;

  @media (max-width: 768px) {
    padding: 2.5rem 1.5rem;
  }
`;

export const StatementText = styled.p`
  max-width: 780px;
  margin: 0 auto;
  font-size: 1.625rem;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.gray900};

  span {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

export const StatementSub = styled.p`
  max-width: 640px;
  margin: 0.75rem auto 0;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

/* ============================= 1. CONVICTION =============================
   Dark, high-contrast proof section — the case for why filings (not promises)
   move cargo. Intentionally the only dark section on the page. */

export const ConvictionSection = styled.section`
  background: linear-gradient(160deg, #1c130d 0%, #140d08 100%);
  padding: 6rem 4rem;
  scroll-margin-top: 76px;

  @media (max-width: 768px) {
    padding: 3.5rem 1.5rem;
  }
`;

export const ConvictionInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

export const ConvictionEyebrow = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary400};
  margin-bottom: 1.25rem;
`;

export const ConvictionTitle = styled.h2`
  font-size: 2.75rem;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #ffffff;
  margin: 0 0 1.75rem 0;

  @media (max-width: 1100px) {
    font-size: 2.25rem;
  }
`;

export const ConvictionText = styled.p`
  font-size: 1.0625rem;
  line-height: 1.7;
  color: #c9bdb0;
  margin: 0 0 1.5rem 0;
  max-width: 620px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

export const StatCell = styled.div`
  padding: 2rem 1.75rem;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:nth-child(2n) {
    border-right: none;
  }
  &:nth-child(3),
  &:nth-child(4) {
    border-bottom: none;
  }
`;

export const StatValue = styled.div<{ $accent?: boolean }>`
  font-size: 2.25rem;
  font-weight: 800;
  color: ${({ theme, $accent }) => ($accent ? theme.colors.primary400 : '#ffffff')};
  margin-bottom: 0.5rem;
`;

export const StatLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a7e72;
`;

/* =============================== 2. FLOW =================================
   "How it works" — the order lifecycle as a single horizontal roadmap. */

export const FlowSection = styled.section`
  padding: 6rem 4rem;
  background-color: ${({ theme }) => theme.colors.surface};
  scroll-margin-top: 76px;

  @media (max-width: 768px) {
    padding: 3.5rem 1.5rem;
  }
`;

export const FlowInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

export const FlowHeader = styled.div`
  max-width: 640px;
  margin: 0 0 3.5rem 0;
`;

export const FlowEyebrow = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1rem;
`;

export const FlowTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0 0 0.75rem 0;
`;

export const FlowSubtitle = styled.p`
  font-size: 1.0625rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

export const FlowStepper = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  position: relative;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(4, 1fr);
    row-gap: 2rem;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1.75rem;
  }
`;

export const FlowStep = styled.div`
  position: relative;
  padding-right: 1.5rem;

  &:last-child {
    padding-right: 0;
  }

  @media (max-width: 900px) {
    padding-right: 0;
    display: flex;
    gap: 1rem;
  }
`;

export const FlowConnector = styled.div`
  position: absolute;
  top: 22px;
  left: 22px;
  right: 0;
  height: 2px;
  background: ${({ theme }) => theme.colors.borderStrong};

  ${FlowStep}:last-child & {
    display: none;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

export const FlowStepCircle = styled.div<{ $final?: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme, $final }) => ($final ? theme.colors.primary : theme.colors.gray900)};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9375rem;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  margin-bottom: 1rem;

  @media (max-width: 900px) {
    margin-bottom: 0;
  }
`;

export const FlowStepLabel = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
  margin-bottom: 0.25rem;
`;

export const FlowStepDesc = styled.div`
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding-right: 0.5rem;
`;

/* ============================= 3. PLATFORM ================================
   Functionality expansion — one accent color only, no pastel rainbow. */

export const FeaturesSection = styled.section`
  padding: 6rem 4rem;
  background-color: ${({ theme }) => theme.colors.background};
  scroll-margin-top: 76px;

  @media (max-width: 768px) {
    padding: 3.5rem 1.5rem;
  }
`;

export const FeaturesInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

export const SectionEyebrow = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1rem;
  text-align: center;
`;

export const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.25rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.gray900};
  margin-bottom: 0.75rem;
`;

export const SectionSubtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.0625rem;
  max-width: 640px;
  margin: 0 auto 3.5rem auto;
  line-height: 1.6;
`;

export const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const FeatureCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: 1.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary300};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

export const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  background-color: ${({ theme }) => theme.colors.primary50};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary700};
  margin-bottom: 1.25rem;
`;

export const FeatureTitle = styled.h3`
  font-size: 1.0625rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0 0 0.5rem 0;
`;

export const FeatureDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0;
`;

/* ============================= 4. PARTNERS ================================ */

export const PartnersSection = styled.section`
  padding: 5rem 4rem;
  background-color: ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSubtle};

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

export const PartnersInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

export const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  border: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const LogoCell = styled.div`
  padding: 1.75rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  border-right: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  text-align: center;
`;

export const LogoEmblem = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $color }) => $color};
  color: #ffffff;
  font-size: 0.8125rem;
  font-weight: 800;
  letter-spacing: 0.01em;
`;

export const LogoImage = styled.img`
  height: 44px;
  width: auto;
  max-width: 108px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  object-fit: contain;
`;

export const LogoName = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.colors.gray700};
`;

/* ============================ 5. EXPORT LANE ===============================
   The buyer-facing close — placed near the bottom of the page. */

export const ExportLaneSection = styled.section`
  padding: 6rem 4rem;
  background: ${({ theme }) => theme.colors.primary50};
  scroll-margin-top: 76px;

  @media (max-width: 768px) {
    padding: 3.5rem 1.5rem;
  }
`;

export const ExportLaneInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

export const ExportEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.875rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.primary200};
  color: ${({ theme }) => theme.colors.primary700};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
`;

export const ExportTitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0 0 1.5rem 0;

  @media (max-width: 1100px) {
    font-size: 2.5rem;
  }
`;

export const ExportText = styled.p`
  font-size: 1.0625rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.gray700};
  margin: 0 0 2rem 0;
  max-width: 560px;

  b {
    color: ${({ theme }) => theme.colors.gray900};
  }
`;

export const ExportCtaRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 0.875rem;
  flex-wrap: wrap;
`;

export const ExportPriceNote = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.colors.gray500};
  margin-bottom: 2.5rem;
`;

export const PillRow = styled.div`
  display: flex;
  gap: 0.625rem;
  flex-wrap: wrap;
`;

export const PillTag = styled.div`
  padding: 0.5rem 0.875rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray600};
`;

export const OrderCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  overflow: hidden;
`;

export const PlatformScreenshot = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSubtle};
`;

export const OrderCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSubtle};
`;

export const OrderCardId = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.colors.gray700};
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
`;

export const OrderStatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.successLight};
  color: ${({ theme }) => theme.colors.successDark};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.success};
  }
`;

export const OrderChecklist = styled.div`
  padding: 0.5rem 0;
`;

export const OrderCheckItem = styled.div`
  display: flex;
  gap: 0.875rem;
  align-items: flex-start;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSubtle};

  &:last-child {
    border-bottom: none;
  }
`;

export const OrderCheckIcon = styled.div<{ $state: 'done' | 'active' | 'pending' }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 0.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $state }) =>
    $state === 'done' ? theme.colors.successLight : $state === 'active' ? theme.colors.primary100 : theme.colors.gray100};
  color: ${({ theme, $state }) =>
    $state === 'done' ? theme.colors.successDark : $state === 'active' ? theme.colors.primary700 : theme.colors.gray400};

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const OrderCheckText = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
  margin-bottom: 0.125rem;
`;

export const OrderCheckMeta = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
`;

export const OrderCardFooter = styled.div`
  padding: 1.125rem 1.5rem;
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const OrderCardFooterText = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  max-width: 320px;
`;

export const LanguageRow = styled.div`
  display: flex;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary700};
  white-space: nowrap;
`;

/* ============================= 6. PRICING ================================= */

export const PricingSection = styled.section`
  padding: 6rem 4rem;
  background-color: ${({ theme }) => theme.colors.gray900};
  text-align: center;
  scroll-margin-top: 76px;

  @media (max-width: 768px) {
    padding: 3.5rem 1.5rem;
  }
`;

export const PricingInner = styled.div`
  max-width: 1160px;
  margin: 0 auto;
`;

export const PricingEyebrow = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary400};
  margin-bottom: 1.25rem;
`;

export const PricingHeadline = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #ffffff;
  margin-bottom: 0.75rem;

  span {
    color: ${({ theme }) => theme.colors.primary400};
  }

  @media (max-width: 600px) {
    font-size: 2rem;
  }
`;

export const PricingSub = styled.p`
  font-size: 1.0625rem;
  color: #a8b0bd;
  margin: 0 auto 3rem;
  line-height: 1.6;
  max-width: 900px;
  text-align: left;
`;

export const PricingNote = styled.div`
  font-size: 0.8125rem;
  color: #6b7686;
  margin-top: 2.5rem;
`;

export const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
  text-align: left;

  @media (max-width: 1080px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

export const PricingCard = styled.div<{ $featured?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 2rem 1.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ $featured }) => ($featured ? 'rgba(242, 105, 31, 0.08)' : 'rgba(255, 255, 255, 0.03)')};
  border: 1.5px solid ${({ $featured, theme }) => ($featured ? theme.colors.primary400 : 'rgba(255, 255, 255, 0.08)')};
`;

export const PricingBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 1.75rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const PricingCardName = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary400};
  margin-bottom: 0.875rem;
`;

export const PricingCardPrice = styled.div`
  font-size: 2.25rem;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.02em;
  margin-bottom: 0.25rem;

  span {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #8891a0;
    letter-spacing: 0;
  }
`;

export const PricingCardMeta = styled.div`
  font-size: 0.8125rem;
  color: #8891a0;
  margin-bottom: 1.5rem;
  line-height: 1.5;
  min-height: 38px;
`;

export const PricingCardFeatureList = styled.ul`
  list-style: none;
  margin: 0 0 1.75rem 0;
  padding: 0;
  flex: 1;
`;

export const PricingCardFeature = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  font-size: 0.875rem;
  color: #d3d8e0;
  line-height: 1.5;
  margin-bottom: 0.75rem;

  svg {
    flex-shrink: 0;
    margin-top: 0.1875rem;
    color: ${({ theme }) => theme.colors.primary400};
  }
`;

export const PricingCardCta = styled.button<{ $filled?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s, border-color 0.15s;
  background: ${({ $filled, theme }) => ($filled ? theme.colors.primary : 'transparent')};
  color: ${({ $filled }) => ($filled ? '#ffffff' : '#ffffff')};
  border: 1.5px solid ${({ $filled, theme }) => ($filled ? theme.colors.primary : 'rgba(255, 255, 255, 0.2)')};

  &:hover {
    opacity: 0.9;
    border-color: ${({ theme }) => theme.colors.primary400};
  }
`;

export const PricingCardFootnote = styled.div`
  font-size: 0.75rem;
  color: #6b7686;
  margin-top: 0.75rem;
  text-align: center;
`;

/* =============================== FOOTER ==================================== */

export const FooterSection = styled.footer`
  background-color: ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  padding: 1.75rem 4rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem;
  }
`;

export const FooterBottom = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

export const FooterLink = styled.a`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  transition: color 0.15s;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;
